import * as React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getMatchAdminHistory, getMatchAdminPlayers, updateMatchAdminHistory } from '../../Api';
import { AdminMatchHistoryDto, AdminMatchPlayerInfoDto, AdminMatchPlayersDto } from '../../models/dtos/AdminMatchStatDto';
import { CenteredProgress, EmptyState, PlayerAvatar, formatLabel } from './MatchAdminTabCommon';
import { MATCH_HISTORY_TYPE_OPTIONS } from './matchAdminOptions';

type EditableHistoryRow = {
    rowId: string;
    minute: string;
    type: string;
    playerId: string;
    assistantId: string;
};

function toDraftRows(history: AdminMatchHistoryDto['eventHistory']): EditableHistoryRow[] {
    return history.map((item, index) => ({
        rowId: `${item.minute}-${item.playerId ?? 'history'}-${index}`,
        minute: item.minute.toString(),
        type: item.type ?? 'RegularGoal',
        playerId: item.playerId ?? '',
        assistantId: item.assistantId ?? ''
    }));
}

function toPayload(rows: EditableHistoryRow[]): AdminMatchHistoryDto['eventHistory'] {
    return rows.map(row => ({
        minute: Number(row.minute),
        type: row.type,
        playerId: row.playerId || null,
        assistantId: row.assistantId || null
    }));
}

function SortableHistoryRow(props: {
    row: EditableHistoryRow;
    playerOptions: AdminMatchPlayerInfoDto[];
    onUpdate: (patch: Partial<EditableHistoryRow>) => void;
    onDelete: () => void;
}) {
    const { row, playerOptions, onUpdate, onDelete } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.rowId });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const selectedPlayer = playerOptions.find(p => p.id === row.playerId) ?? null;
    const selectedAssistant = playerOptions.find(p => p.id === row.assistantId) ?? null;

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 32, cursor: 'grab', color: 'text.disabled' }} {...attributes} {...listeners}>
                <DragHandleIcon fontSize="small" />
            </TableCell>
            <TableCell sx={{ minWidth: 120 }}>
                <TextField size="small" type="number" fullWidth value={row.minute} onChange={event => onUpdate({ minute: event.target.value })} />
            </TableCell>
            <TableCell sx={{ minWidth: 220 }}>
                <TextField select fullWidth size="small" value={row.type} onChange={event => onUpdate({ type: event.target.value })}>
                    {MATCH_HISTORY_TYPE_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                            {formatLabel(option)}
                        </MenuItem>
                    ))}
                </TextField>
            </TableCell>
            <TableCell sx={{ minWidth: 260 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <PlayerAvatar player={selectedPlayer} size={28} />
                    <TextField select fullWidth size="small" value={row.playerId} onChange={event => onUpdate({ playerId: event.target.value })}>
                        <MenuItem value="">No player</MenuItem>
                        {playerOptions.map(player => (
                            <MenuItem key={player.id} value={player.id}>
                                {player.name || player.id}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 260 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <PlayerAvatar player={selectedAssistant} size={28} />
                    <TextField select fullWidth size="small" value={row.assistantId} onChange={event => onUpdate({ assistantId: event.target.value })}>
                        <MenuItem value="">No assistant</MenuItem>
                        {row.assistantId && selectedAssistant === null && (
                            <MenuItem value={row.assistantId}>{row.assistantId}</MenuItem>
                        )}
                        {playerOptions.map(player => (
                            <MenuItem key={player.id} value={player.id}>
                                {player.name || player.id}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 180 }}>
                {selectedPlayer?.id || '-'}
            </TableCell>
            <TableCell align="right">
                <IconButton color="error" onClick={onDelete}>
                    <DeleteOutlineIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

export default function MatchHistoryTab(props: {
    matchId: number;
    onError: (message: string) => void;
    onSuccess: (message: string) => void;
}) {
    const { matchId, onError, onSuccess } = props;
    const [adminHistory, setAdminHistory] = React.useState<AdminMatchHistoryDto | null>(null);
    const [adminPlayers, setAdminPlayers] = React.useState<AdminMatchPlayersDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [draftRows, setDraftRows] = React.useState<EditableHistoryRow[]>([]);
    const [saving, setSaving] = React.useState(false);

    const sensors = useSensors(useSensor(PointerSensor));

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over != null && active.id !== over.id) {
            setDraftRows(current => {
                const oldIndex = current.findIndex(row => row.rowId === active.id);
                const newIndex = current.findIndex(row => row.rowId === over.id);
                return arrayMove(current, oldIndex, newIndex);
            });
        }
    };

    React.useEffect(() => {
        setLoading(true);
        Promise.all([getMatchAdminHistory(matchId), getMatchAdminPlayers(matchId)])
            .then(([history, players]) => {
                setAdminHistory(history);
                setAdminPlayers(players);
                setLoading(false);
            })
            .catch(() => {
                setAdminHistory(null);
                setAdminPlayers(null);
                setLoading(false);
            });
    }, [matchId]);

    React.useEffect(() => {
        setDraftRows(adminHistory?.eventHistory ? toDraftRows(adminHistory.eventHistory) : []);
    }, [adminHistory]);

    if (loading) {
        return <CenteredProgress />;
    }

    if (adminHistory == null) {
        return <EmptyState message="No history available for the main event." />;
    }

    const payload = toPayload(draftRows);
    const originalPayload = adminHistory.eventHistory.map(item => ({
        minute: item.minute,
        type: item.type ?? null,
        playerId: item.playerId ?? null,
        assistantId: item.assistantId ?? null
    }));
    const isDirty = JSON.stringify(payload) !== JSON.stringify(originalPayload);
    const playerOptions = adminPlayers?.playersInfo ?? [];

    const updateRow = (rowId: string, patch: Partial<EditableHistoryRow>) => {
        setDraftRows(current => current.map(row => row.rowId === rowId ? { ...row, ...patch } : row));
    };

    const onAddRow = () => {
        setDraftRows(current => [{
            rowId: `new-${Date.now()}-${current.length}`,
            minute: '0',
            type: MATCH_HISTORY_TYPE_OPTIONS[0],
            playerId: playerOptions[0]?.id ?? '',
            assistantId: ''
        }, ...current]);
    };

    const onReset = () => {
        setDraftRows(toDraftRows(adminHistory.eventHistory));
    };

    const onSave = async () => {
        if (payload.some(item => !Number.isFinite(item.minute) || item.minute < 0)) {
            onError('Minutes must be valid non-negative numbers.');
            return;
        }

        setSaving(true);
        try {
            const result = await updateMatchAdminHistory(matchId, payload);
            setAdminHistory(prev => prev == null ? prev : { ...prev, eventHistory: result.eventHistory });
            onSuccess('History update sent to provider.');
        } catch (error) {
            onError(error instanceof Error ? error.message : 'Failed to update history.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
                Main variety: {adminHistory.varietyName}
            </Typography>
            <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddRow}>Add event</Button>
                <Button variant="outlined" color="inherit" startIcon={<RestartAltIcon />} onClick={onReset} disabled={!isDirty || saving}>Reset</Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={!isDirty || saving}>Save</Button>
            </Stack>
            {draftRows.length === 0 ? (
                <EmptyState message="No events available for the main event." />
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={draftRows.map(r => r.rowId)} strategy={verticalListSortingStrategy}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 32 }} />
                                        <TableCell>Minute</TableCell>
                                        <TableCell>Event Type</TableCell>
                                        <TableCell>Player</TableCell>
                                        <TableCell>Assistant</TableCell>
                                        <TableCell>Player ID</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {draftRows.map(row => (
                                        <SortableHistoryRow
                                            key={row.rowId}
                                            row={row}
                                            playerOptions={playerOptions}
                                            onUpdate={patch => updateRow(row.rowId, patch)}
                                            onDelete={() => setDraftRows(current => current.filter(item => item.rowId !== row.rowId))}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </SortableContext>
                    </DndContext>
                </Box>
            )}
        </Stack>
    );
}
